import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  RefreshCw,
  Receipt,
  FileJson,
  Download,
  Calendar,
  Building2,
  Users,
  ShoppingCart,
  IndianRupee
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../App';

const GSTReports = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');
  const [reportData, setReportData] = useState(null);
  
  // Period selection
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(String(currentDate.getMonth() + 1).padStart(2, '0'));
  const [selectedYear, setSelectedYear] = useState(String(currentDate.getFullYear()));

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => String(currentDate.getFullYear() - 2 + i));

  useEffect(() => {
    fetchReport();
  }, [selectedMonth, selectedYear, activeTab]);

  const getReturnPeriod = () => `${selectedMonth}${selectedYear}`;

  const fetchReport = async () => {
    try {
      setLoading(true);
      const period = getReturnPeriod();
      let endpoint = '';
      
      switch (activeTab) {
        case 'summary':
          endpoint = `/finance/gst/summary?return_period=${period}`;
          break;
        case 'gstr1':
          endpoint = `/finance/gst/gstr1-summary?return_period=${period}`;
          break;
        default:
          endpoint = `/finance/gst/summary?return_period=${period}`;
      }
      
      const response = await api.get(endpoint);
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to fetch GST report:', error);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const handleExportJSON = () => {
    if (!reportData) return;
    
    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `GST_${activeTab}_${selectedMonth}${selectedYear}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('JSON exported successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/finance')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">GST Reports</h1>
            <p className="text-gray-600">India GST Compliance Reports</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportJSON}>
            <FileJson className="h-4 w-4 mr-2" />
            Export JSON
          </Button>
        </div>
      </div>

      {/* Period Selection */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-400" />
              <Label>Return Period:</Label>
            </div>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map(month => (
                  <SelectItem key={month.value} value={month.value}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchReport}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="summary" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            GST Summary
          </TabsTrigger>
          <TabsTrigger value="gstr1" className="flex items-center gap-2">
            <FileJson className="h-4 w-4" />
            GSTR-1
          </TabsTrigger>
          <TabsTrigger value="gstr3b" className="flex items-center gap-2">
            <FileJson className="h-4 w-4" />
            GSTR-3B
          </TabsTrigger>
        </TabsList>

        {/* GST Summary */}
        <TabsContent value="summary">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-700">Taxable Value</p>
                    <p className="text-2xl font-bold text-blue-800">
                      {formatCurrency(reportData?.outward_supplies?.taxable_value)}
                    </p>
                  </div>
                  <IndianRupee className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-700">CGST</p>
                    <p className="text-2xl font-bold text-green-800">
                      {formatCurrency(reportData?.outward_supplies?.cgst)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-700">SGST</p>
                    <p className="text-2xl font-bold text-orange-800">
                      {formatCurrency(reportData?.outward_supplies?.sgst)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-700">Total Tax</p>
                    <p className="text-2xl font-bold text-purple-800">
                      {formatCurrency(reportData?.outward_supplies?.total_tax)}
                    </p>
                  </div>
                  <Receipt className="h-8 w-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Outward Supplies Summary</CardTitle>
              <CardDescription>
                Period: {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Description</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3">Taxable Value</td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatCurrency(reportData?.outward_supplies?.taxable_value)}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3">CGST</td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatCurrency(reportData?.outward_supplies?.cgst)}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3">SGST</td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatCurrency(reportData?.outward_supplies?.sgst)}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3">IGST</td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatCurrency(reportData?.outward_supplies?.igst)}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3">Cess</td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatCurrency(reportData?.outward_supplies?.cess)}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot className="bg-gray-100">
                    <tr>
                      <td className="px-4 py-3 font-bold">Total Tax Liability</td>
                      <td className="px-4 py-3 text-right font-bold">
                        {formatCurrency(reportData?.outward_supplies?.total_tax)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              
              <div className="mt-4 text-sm text-gray-500">
                Sales Invoices in period: {reportData?.sales_count || 0}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* GSTR-1 */}
        <TabsContent value="gstr1">
          <div className="space-y-6">
            {/* B2B Summary */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  <CardTitle>B2B Invoices</CardTitle>
                </div>
                <CardDescription>
                  Business to Business supplies (with GSTIN)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {reportData?.b2b?.count > 0 ? (
                  <>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-600">Invoice Count</p>
                        <p className="text-2xl font-bold text-blue-800">{reportData.b2b.count}</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-600">Taxable Value</p>
                        <p className="text-2xl font-bold text-blue-800">{formatCurrency(reportData.b2b.taxable_value)}</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-600">Total Tax</p>
                        <p className="text-2xl font-bold text-blue-800">{formatCurrency(reportData.b2b.total_tax)}</p>
                      </div>
                    </div>
                    
                    {reportData.b2b.entries?.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-medium">Invoice No.</th>
                              <th className="px-4 py-2 text-left text-sm font-medium">Date</th>
                              <th className="px-4 py-2 text-left text-sm font-medium">GSTIN</th>
                              <th className="px-4 py-2 text-left text-sm font-medium">Party</th>
                              <th className="px-4 py-2 text-right text-sm font-medium">Taxable</th>
                              <th className="px-4 py-2 text-right text-sm font-medium">Tax</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {reportData.b2b.entries.map((entry, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-sm">{entry.invoice_number}</td>
                                <td className="px-4 py-2 text-sm">{entry.invoice_date}</td>
                                <td className="px-4 py-2 text-sm font-mono">{entry.gstin}</td>
                                <td className="px-4 py-2 text-sm">{entry.party_name}</td>
                                <td className="px-4 py-2 text-sm text-right">{formatCurrency(entry.taxable_value)}</td>
                                <td className="px-4 py-2 text-sm text-right">{formatCurrency(entry.cgst + entry.sgst + entry.igst)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No B2B invoices for this period</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* B2C Summary */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-green-600" />
                  <CardTitle>B2C Invoices</CardTitle>
                </div>
                <CardDescription>
                  Business to Consumer supplies (without GSTIN)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600">Invoice Count</p>
                    <p className="text-2xl font-bold text-green-800">{reportData?.b2c_small?.count || 0}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600">Taxable Value</p>
                    <p className="text-2xl font-bold text-green-800">{formatCurrency(reportData?.b2c_small?.taxable_value)}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  Note: B2C Small supplies are reported in aggregate format in GSTR-1
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* GSTR-3B */}
        <TabsContent value="gstr3b">
          <Card>
            <CardHeader>
              <CardTitle>GSTR-3B Summary Return</CardTitle>
              <CardDescription>
                Monthly summary return for {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Section 3.1 */}
              <div>
                <h4 className="font-medium text-gray-700 mb-3">3.1 - Details of Outward Supplies</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium">Nature of Supplies</th>
                        <th className="px-4 py-2 text-right text-sm font-medium">Taxable Value</th>
                        <th className="px-4 py-2 text-right text-sm font-medium">IGST</th>
                        <th className="px-4 py-2 text-right text-sm font-medium">CGST</th>
                        <th className="px-4 py-2 text-right text-sm font-medium">SGST</th>
                        <th className="px-4 py-2 text-right text-sm font-medium">Cess</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-2">(a) Outward taxable supplies</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(reportData?.outward_supplies?.taxable_value)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(reportData?.outward_supplies?.igst)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(reportData?.outward_supplies?.cgst)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(reportData?.outward_supplies?.sgst)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(reportData?.outward_supplies?.cess)}</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-2">(b) Zero rated supplies</td>
                        <td className="px-4 py-2 text-right">₹0.00</td>
                        <td className="px-4 py-2 text-right">₹0.00</td>
                        <td className="px-4 py-2 text-right">₹0.00</td>
                        <td className="px-4 py-2 text-right">₹0.00</td>
                        <td className="px-4 py-2 text-right">₹0.00</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-2">(c) Nil rated, exempted</td>
                        <td className="px-4 py-2 text-right">₹0.00</td>
                        <td className="px-4 py-2 text-right">-</td>
                        <td className="px-4 py-2 text-right">-</td>
                        <td className="px-4 py-2 text-right">-</td>
                        <td className="px-4 py-2 text-right">-</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Section 5 - Tax Payable */}
              <div>
                <h4 className="font-medium text-gray-700 mb-3">5 - Tax Payable</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium">Description</th>
                        <th className="px-4 py-2 text-right text-sm font-medium">IGST</th>
                        <th className="px-4 py-2 text-right text-sm font-medium">CGST</th>
                        <th className="px-4 py-2 text-right text-sm font-medium">SGST</th>
                        <th className="px-4 py-2 text-right text-sm font-medium">Cess</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-2">Tax Payable</td>
                        <td className="px-4 py-2 text-right font-medium">{formatCurrency(reportData?.outward_supplies?.igst)}</td>
                        <td className="px-4 py-2 text-right font-medium">{formatCurrency(reportData?.outward_supplies?.cgst)}</td>
                        <td className="px-4 py-2 text-right font-medium">{formatCurrency(reportData?.outward_supplies?.sgst)}</td>
                        <td className="px-4 py-2 text-right font-medium">{formatCurrency(reportData?.outward_supplies?.cess)}</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-2">Less: ITC Utilised</td>
                        <td className="px-4 py-2 text-right">₹0.00</td>
                        <td className="px-4 py-2 text-right">₹0.00</td>
                        <td className="px-4 py-2 text-right">₹0.00</td>
                        <td className="px-4 py-2 text-right">₹0.00</td>
                      </tr>
                    </tbody>
                    <tfoot className="bg-gray-100">
                      <tr>
                        <td className="px-4 py-2 font-bold">Net Tax Payable</td>
                        <td className="px-4 py-2 text-right font-bold">{formatCurrency(reportData?.outward_supplies?.igst)}</td>
                        <td className="px-4 py-2 text-right font-bold">{formatCurrency(reportData?.outward_supplies?.cgst)}</td>
                        <td className="px-4 py-2 text-right font-bold">{formatCurrency(reportData?.outward_supplies?.sgst)}</td>
                        <td className="px-4 py-2 text-right font-bold">{formatCurrency(reportData?.outward_supplies?.cess)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> This is a preview of GSTR-3B data. For actual filing, please verify all details 
                  and use the official GST portal or an authorized GSP.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GSTReports;
